import type { LevelReading } from '../../../audio/api';
import type { StudioApi } from '../../../engine/api';

type Listener = (levels: Readonly<Record<string, LevelReading>>, dtMs: number) => void;

interface Ticker {
  subscribe(listener: Listener): () => void;
}

const tickers = new WeakMap<object, Ticker>();

/**
 * One requestAnimationFrame loop per studio shared by every meter, running
 * only while at least one meter is mounted. Meters draw straight to their
 * canvases from here; React is not involved per frame.
 */
export function meterTicker(studio: Pick<StudioApi, 'getLevels'>): Ticker {
  const existing = tickers.get(studio);
  if (existing) return existing;

  const listeners = new Set<Listener>();
  let handle = 0;
  let last = 0;

  const frame = (now: number): void => {
    const dt = last === 0 ? 16 : now - last;
    last = now;
    let levels: Readonly<Record<string, LevelReading>> = {};
    try {
      levels = studio.getLevels();
    } catch {
      // The engine may be mid-teardown; draw silence for this frame.
    }
    for (const listener of [...listeners]) listener(levels, dt);
    handle = listeners.size > 0 ? requestAnimationFrame(frame) : 0;
  };

  const ticker: Ticker = {
    subscribe(listener) {
      listeners.add(listener);
      if (handle === 0) {
        last = 0;
        handle = requestAnimationFrame(frame);
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && handle !== 0) {
          cancelAnimationFrame(handle);
          handle = 0;
        }
      };
    },
  };
  tickers.set(studio, ticker);
  return ticker;
}
